/**
 * Skills command handlers
 */

import { readFileSync } from 'fs';
import { createSkillService } from '../utils/client.js';
import { chalk, formatOutput, formatSuccess, formatTable, isInteractiveOutput } from '../utils/format.js';
import { handleApiError, requireOption } from '../utils/errors.js';
import type { Skill } from '../../../../src/lib/services/skill.service.js';

type SkillScope = 'user' | 'project';

interface CreateSkillOptions {
  name?: string;
  slug?: string;
  body?: string;
  description?: string;
  argumentHint?: string;
  scope: SkillScope;
  projectId?: number;
  markdownPath?: string;
  enabled?: boolean;
}

interface UpdateSkillOptions {
  name?: string;
  slug?: string;
  body?: string;
  description?: string;
  argumentHint?: string;
  markdownPath?: string;
  enabled?: boolean;
}

interface ImportSkillsOptions {
  scope: SkillScope;
  projectId?: number;
  dryRun?: boolean;
  slugs?: string[];
}

function formatSkillTable(skills: Skill[]): string {
  const termWidth = process.stdout.columns || 120;
  const descriptionWidth = Math.max(30, Math.floor(termWidth * 0.3));
  const rows = skills.map((skill) => [
    skill.id,
    skill.projectId == null ? 'Personal' : `Project ${skill.projectId}`,
    `/${skill.slug}`,
    skill.name,
    skill.enabled ? 'Yes' : 'No',
    skill.description ?? '',
  ]);

  return formatTable(
    ['ID', 'Scope', 'Slug', 'Name', 'Enabled', 'Description'],
    rows,
    [undefined, undefined, undefined, undefined, undefined, descriptionWidth]
  );
}

function printSkills(skills: Skill[], title: string): void {
  if (isInteractiveOutput()) {
    console.log('');
    console.log(chalk.bold.white(`  ${title}`));
  }
  console.log(formatSkillTable(skills));
  if (isInteractiveOutput()) console.log('');
}

function readMarkdown(markdownPath?: string): string | undefined {
  return markdownPath ? readFileSync(markdownPath, 'utf8') : undefined;
}

export async function listSkills(
  projectId?: number,
  token?: string,
  apiUrl?: string,
  json?: boolean
): Promise<void> {
  try {
    const service = await createSkillService(token, apiUrl);
    const result = await service.listSkills({ project_id: projectId });

    if (json) {
      console.log(formatOutput(result, true));
    } else if (result.skills && result.skills.length > 0) {
      printSkills(result.skills, projectId == null ? 'Personal skills' : `Project ${projectId} skills`);
    } else {
      console.log(chalk.dim('\n  No skills found\n'));
    }
  } catch (error) {
    handleApiError(error);
  }
}

export async function getSkill(
  skillId: number,
  token?: string,
  apiUrl?: string,
  json?: boolean
): Promise<void> {
  try {
    requireOption(skillId.toString(), 'skill-id');
    const service = await createSkillService(token, apiUrl);
    const result = await service.getSkill({ skill_id: skillId });

    if (json) {
      console.log(formatOutput(result, true));
    } else {
      printSkills([result.skill], 'Skill');
    }
  } catch (error) {
    handleApiError(error);
  }
}

export async function createSkill(
  options: CreateSkillOptions,
  token?: string,
  apiUrl?: string,
  json?: boolean
): Promise<void> {
  try {
    const service = await createSkillService(token, apiUrl);
    const result = await service.createSkill({
      scope: options.scope,
      project_id: options.projectId,
      markdown: readMarkdown(options.markdownPath),
      name: options.name,
      slug: options.slug,
      body: options.body,
      description: options.description,
      argument_hint: options.argumentHint,
      enabled: options.enabled,
    });

    if (json) {
      console.log(formatOutput(result, true));
    } else {
      console.log(formatSuccess(`Created skill /${result.skill.slug}`));
      printSkills([result.skill], 'Created skill');
    }
  } catch (error) {
    handleApiError(error);
  }
}

export async function updateSkill(
  skillId: number,
  options: UpdateSkillOptions,
  token?: string,
  apiUrl?: string,
  json?: boolean
): Promise<void> {
  try {
    requireOption(skillId.toString(), 'skill-id');
    const service = await createSkillService(token, apiUrl);
    const result = await service.updateSkill({
      skill_id: skillId,
      name: options.name,
      slug: options.slug,
      body: options.body,
      description: options.description,
      argument_hint: options.argumentHint,
      enabled: options.enabled,
      markdown: readMarkdown(options.markdownPath),
    });

    if (json) {
      console.log(formatOutput(result, true));
    } else {
      console.log(formatSuccess(`Updated skill ${skillId}`));
      printSkills([result.skill], 'Updated skill');
    }
  } catch (error) {
    handleApiError(error);
  }
}

export async function deleteSkill(
  skillId: number,
  token?: string,
  apiUrl?: string,
  json?: boolean
): Promise<void> {
  try {
    requireOption(skillId.toString(), 'skill-id');
    const service = await createSkillService(token, apiUrl);
    const result = await service.deleteSkill({ skill_id: skillId });

    if (json) {
      console.log(formatOutput(result, true));
    } else {
      console.log(formatSuccess(`Deleted skill ${skillId}`));
    }
  } catch (error) {
    handleApiError(error);
  }
}

export async function importSkills(
  url: string,
  options: ImportSkillsOptions,
  token?: string,
  apiUrl?: string,
  json?: boolean
): Promise<void> {
  try {
    requireOption(url, 'github-url');
    const service = await createSkillService(token, apiUrl);
    const result = await service.importSkills({
      url,
      scope: options.scope,
      project_id: options.projectId,
      dry_run: options.dryRun,
      slugs: options.slugs,
    });

    if (json) {
      console.log(formatOutput(result, true));
    } else if (result.skills && result.skills.length > 0) {
      printSkills(result.skills, options.dryRun ? 'Skills available to import' : 'Imported skills');
    } else {
      console.log(chalk.dim('\n  No skills found\n'));
    }
  } catch (error) {
    handleApiError(error);
  }
}
